/**
 * Notification Utilities
 *
 * Helper functions for notification display:
 * - Date formatting (relative time)
 * - Text truncation
 */

/**
 * Format notification date to relative time
 *
 * Converts ISO date string to human-readable relative time:
 * - "Just now" / "À l'instant" (< 1 minute)
 * - "5m ago" / "Il y a 5m" (< 1 hour)
 * - "2h ago" / "Il y a 2h" (< 24 hours)
 * - "Yesterday" / "Hier" (yesterday)
 * - "Jan 15" (older)
 *
 * @param isoDate - ISO 8601 date string
 * @param locale - Language locale ('en' or 'fr')
 * @returns Formatted relative time string
 *
 * @example
 * formatNotificationDate('2024-01-20T10:30:00Z', 'en') // => "5m ago"
 * formatNotificationDate('2024-01-19T10:30:00Z', 'fr') // => "Hier"
 */
export function formatNotificationDate(isoDate: string, locale: string): string {
  const now = new Date();
  const date = new Date(isoDate);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Just now (< 1 minute)
  if (diffMins < 1) {
    return locale === 'fr' ? "À l'instant" : 'Just now';
  }

  // Minutes ago (< 1 hour)
  if (diffMins < 60) {
    return locale === 'fr' ? `Il y a ${diffMins}m` : `${diffMins}m ago`;
  }

  // Hours ago (< 24 hours)
  if (diffHours < 24) {
    return locale === 'fr' ? `Il y a ${diffHours}h` : `${diffHours}h ago`;
  }

  // Yesterday
  if (diffDays === 1) {
    return locale === 'fr' ? 'Hier' : 'Yesterday';
  }

  // Older - show date (e.g., "Jan 15")
  const monthNames = locale === 'fr'
    ? ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const month = monthNames[date.getMonth()];
  const day = date.getDate();

  return `${month} ${day}`;
}

/**
 * Truncate text to maximum length
 *
 * Adds ellipsis (...) if text exceeds maxLength.
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 100)
 * @returns Truncated text with ellipsis if needed
 *
 * @example
 * truncateText('This is a very long notification message', 20)
 * // => "This is a very lo..."
 */
export function truncateText(text: string, maxLength: number = 100): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}
